import { Loader2, Pause, Play } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { cn } from "@/v2/lib/utils";
import {
	useEntriesTimer,
	useEntriesTimerDisplaySeconds,
} from "./EntriesTimerContext";

function formatTime(totalSeconds: number): string {
	const h = Math.floor(totalSeconds / 3600);
	const m = Math.floor((totalSeconds % 3600) / 60);
	const s = totalSeconds % 60;
	const mm = String(m).padStart(2, "0");
	const ss = String(s).padStart(2, "0");
	return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * Inline timer for the Entries toolbar, bound to a single entry via
 * `useEntriesTimer`. Idle shows a Start button that creates a blank entry and
 * begins timing it; once running it becomes a blue pill showing the entry's live
 * total (base + elapsed) with a single stop control — a span has no paused
 * state, so stopping is the only way to finish.
 */
export function EntriesTimer() {
	const { activeEntryId, isRunning, isBusy, startNewBlankEntry, pause } =
		useEntriesTimer();
	const displaySeconds = useEntriesTimerDisplaySeconds();

	if (activeEntryId === null) {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="outline"
							size="icon"
							onClick={startNewBlankEntry}
							disabled={isBusy}
							aria-label="Start a timer"
						>
							{isBusy ? (
								<Loader2 className="motion-safe:animate-spin" />
							) : (
								<Play className="fill-current" />
							)}
						</Button>
					</TooltipTrigger>
					<TooltipContent side="bottom">Start a timer</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}

	return (
		<div className="flex h-10 items-center gap-1.5 rounded-md border border-ring bg-background pl-3 pr-1">
			<div className="flex items-center gap-1.5">
				<div
					className={cn(
						"size-2 rounded-full bg-ring",
						isRunning && "motion-safe:animate-pulse",
					)}
				/>
				<span className="tabular-nums text-sm font-semibold text-foreground">
					{formatTime(displaySeconds)}
				</span>
			</div>
			<div className="flex items-center gap-0.5 pl-1">
				<PillButton onClick={pause} label="Pause timer">
					<Pause className="size-4" />
				</PillButton>
			</div>
		</div>
	);
}

function PillButton({
	onClick,
	label,
	children,
}: {
	onClick: () => void;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						type="button"
						onClick={onClick}
						aria-label={label}
						className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground active:bg-accent/80"
					>
						{children}
					</button>
				</TooltipTrigger>
				<TooltipContent side="bottom">{label}</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
